/**
 * Il pannello di anteprima che segue il fuoco (03-SCHERMATE-TV §5).
 *
 * **Si abbona al controller per conto suo.** È il motivo per cui esiste come componente
 * separato: una freccia ri-rende questo riquadro e nient'altro. Se l'anteprima stesse
 * dentro la schermata, ogni pressione ri-renderebbe anche la griglia, e i dodicimila
 * canali tornerebbero a pesare a ogni freccia.
 *
 * Il contrassegno in cima è la soluzione al problema vero di una guida: **lo stesso tasto
 * fa cose diverse a seconda di dove sei.** Dirlo prima costa una riga di testo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  cleanChannelName,
  guideAction,
  hasReminder,
  type Channel,
  type GuideAction,
  type ProgramStore,
} from '@player/domain';
import { FONT_STACK, colors, layout } from '../theme.js';
import { ChannelLogo } from '../components/ChannelLogo.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { GuideController, GuideCursor } from './GuideController.js';

const ACTION_KEY: Readonly<Record<GuideAction['kind'], string>> = {
  watch: 'guide.action.watch',
  replay: 'guide.action.replay',
  setReminder: 'guide.action.setReminder',
  reminderSet: 'guide.action.reminderSet',
  unavailable: 'guide.action.unavailable',
};

const ACTION_COLOR: Readonly<Record<GuideAction['kind'], string>> = {
  watch: colors.accent,
  replay: colors.success,
  setReminder: colors.textMuted,
  reminderSet: colors.success,
  unavailable: colors.textFaint,
};

export interface GuidePreviewProps {
  readonly controller: GuideController;
  readonly programs: ProgramStore;
  readonly serverNowMs: number;
  readonly reminderIds: ReadonlySet<string>;
}

export function GuidePreview({
  controller,
  programs,
  serverNowMs,
  reminderIds,
}: GuidePreviewProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState<GuideCursor>(() => controller.currentCursor);

  useEffect(() => controller.onCursorChange(setCursor), [controller]);

  const channel: Channel | null = controller.channelAt(cursor.channelIndex);
  if (!channel) return <div style={{ height: layout.guidePreviewHeight }} />;

  const cleaned = cleanChannelName(channel.name);
  const channelName = cleaned.usable
    ? cleaned.text
    : translator.t('guide.unnamedChannel', { number: channel.number });

  /*
    **Senza programmi si mostra comunque il canale.**

    Prima qui c'era `return <div style={{ height: 170 }} />`: un rettangolo vuoto. Sembra prudente e
    su stage significa che l'anteprima è **sempre** vuota — nessuno dei 60 canali ha una mappatura
    EPG. Chi si muove nella guida non vede cambiare niente in cima, e insieme al riquadro di
    selezione nascosto è l'altra metà di «i canali non sono navigabili».

    Il logo e il nome si sanno sempre. Del programma si dice che non c'è, che è un'informazione.
  */
  const program =
    programs.programCountOf(cursor.channelIndex) === 0
      ? null
      : programs.materialize(cursor.channelIndex, cursor.slot);
  const action = program
    ? guideAction(
        program,
        channel,
        serverNowMs,
        /*
          La chiave è **canale più istante**, non l'identificativo del programma.

          `materialize` sintetizza `${channelId}-${slot}` dalla posizione nel magazzino: confrontarlo
          con gli id archiviati era falso sempre, per costruzione — e l'anteprima scriveva «Imposta
          promemoria» anche su un programma che ne aveva già uno. Vedi `reminders.ts`.
        */
        hasReminder(reminderIds, program.channelId, program.startMs),
      )
    : null;

  return (
    <div style={{ display: 'flex', gap: 36, alignItems: 'flex-start', height: layout.guidePreviewHeight }}>
      {/*
        La card del canale, 300 × 170 come nel prototipo — e il logo la **riempie**.

        Era `contain`: il marchio restava piccolo in mezzo a trecento pixel di vuoto, che non è una
        scelta di disegno, è spazio sprecato sulla card del canale che si sta guardando. `cover`
        ritaglia, ed è il prezzo giusto qui: il riquadro è grande abbastanza perché resti
        riconoscibile.

        Quando il logo manca, dentro c'è **il nome a 40 px** su un fondo che è suo, esattamente come
        nel prototipo. Non le iniziali: `IS` non identifica niente su un catalogo dove venti canali
        cominciano per «IT|».
      */}
      <ChannelLogo
        name={channelName}
        logoUrl={channel.logoUrl}
        widthPx={layout.guidePreviewLogoWidth}
        heightPx={layout.guidePreviewHeight}
        radiusPx={16}
        nameFontPx={40}
        fit="cover"
        colorSeed={channel.id}
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, font: `600 22px ${FONT_STACK}`, color: colors.textMuted }}>
          <span>
            {channel.number} · {channelName}
          </span>
          {action !== null && (
            <span
              style={{
                padding: '4px 12px',
                borderRadius: 8,
                background: ACTION_COLOR[action.kind],
                color: colors.background,
                font: `700 17px ${FONT_STACK}`,
                letterSpacing: '.04em',
              }}
            >
              {translator.t(ACTION_KEY[action.kind])}
            </span>
          )}
        </div>
        <div
          style={{
            font: `700 50px/1.05 ${FONT_STACK}`,
            letterSpacing: '-.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: colors.text,
          }}
        >
          {/* Senza EPG si dice che il programma non si sa — non si scrive il nome del canale al
              posto del titolo, che farebbe credere che il programma si chiami come l'emittente. */}
          {program ? program.title : translator.t('player.noProgram')}
        </div>
        {program && (
          <div style={{ font: `500 23px ${FONT_STACK}`, color: colors.textMuted }}>
            {translator.timeRange(program.startMs, program.endMs)}
            {program.genre ? ` · ${program.genre}` : ''}
          </div>
        )}
        {/*
          La trama, due righe e poi tagliata.

          Non si disegna affatto quando l'EPG non la porta: uno spazio vuoto alto due righe farebbe
          ballare tutto quello che sta sotto a ogni freccia, a seconda che il programma abbia o no una
          descrizione. Il taglio a due righe è del prototipo, e il `maxWidth` serve perché su uno
          schermo largo una riga di trama lunga arriverebbe fino all'orologio.
        */}
        {program !== null && program.description.length > 0 && (
          <div
            data-preview-description="1"
            style={{
              font: `400 22px/1.4 ${FONT_STACK}`,
              color: colors.textDim,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              maxWidth: 980,
            }}
          >
            {program.description}
          </div>
        )}
      </div>
    </div>
  );
}
